import { useEffect, useRef, useState } from 'react';
import {
  AccessibilityInfo,
  Animated,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { isWageIncomeType } from '@perdiem/trpc/income-types';
import {
  displayedSuccessRate,
  type FireLifecycleView,
  type RetirementChoice,
} from '@perdiem/trpc/fire-lifecycle';
import { trpc } from '../lib/trpc';
import { Button } from './Button';
import { TextInput } from './TextInput';
import { ResponsiveModal } from './ResponsiveModal';

const HEALTH_COPY = {
  on_track: 'On track',
  watch: 'Below your target',
  review_needed: 'Review needed',
  needs_review: 'Review your inputs',
};
function Confetti() {
  const progress = useRef(new Animated.Value(0)).current;
  const [reduced, setReduced] = useState(true);
  useEffect(() => {
    void AccessibilityInfo.isReduceMotionEnabled().then(setReduced);
    const subscription = AccessibilityInfo.addEventListener(
      'reduceMotionChanged',
      setReduced,
    );
    return () => subscription.remove();
  }, []);
  useEffect(() => {
    if (reduced) return;
    const animation = Animated.timing(progress, {
      toValue: 1,
      duration: 2400,
      useNativeDriver: true,
    });
    animation.start();
    return () => animation.stop();
  }, [reduced, progress]);
  if (reduced) return null;
  return (
    <View
      pointerEvents="none"
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 240,
        overflow: 'hidden',
      }}
    >
      {Array.from({ length: 22 }, (_, i) => (
        <Animated.View
          key={i}
          style={{
            position: 'absolute',
            left: `${(i * 37) % 100}%`,
            width: 7,
            height: 13,
            backgroundColor: ['#16a34a', '#fbbf24', '#3b82f6', '#f472b6'][
              i % 4
            ],
            opacity: progress.interpolate({
              inputRange: [0, 0.8, 1],
              outputRange: [1, 1, 0],
            }),
            transform: [
              {
                translateY: progress.interpolate({
                  inputRange: [0, 1],
                  outputRange: [-30 - (i % 4) * 12, 250],
                }),
              },
              {
                rotate: progress.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0deg', `${i % 2 ? 360 : -360}deg`],
                }),
              },
            ],
          }}
        />
      ))}
    </View>
  );
}
function RetirementEditor({
  view,
  choice,
  onClose,
}: {
  view: FireLifecycleView;
  choice: RetirementChoice;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const currentYear = new Date().getFullYear();
  const [drafts, setDrafts] = useState(() =>
    view.persons.map((p) => ({
      personId: p.id,
      mode:
        p.actualRetirementYear != null
          ? 'retired'
          : choice === 'already_retired' && p.type === 'primary'
            ? 'retired'
            : choice === 'plan_retirement' && p.type === 'primary'
              ? 'planned'
              : p.targetFireYear != null
                ? 'planned'
                : 'auto',
      year: String(p.actualRetirementYear ?? p.targetFireYear ?? currentYear),
    })),
  );
  const incomeQ = trpc.income.list.useQuery();
  const [continuing, setContinuing] = useState<string[] | null>(null);
  const continuingIds =
    continuing ??
    (incomeQ.data ?? [])
      .filter((s) => s.continuesInRetirement)
      .map((s) => s.id);
  const [confirmed, setConfirmed] = useState(false);
  const save = trpc.fire.setRetirementDecision.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.fire.getLifecycle.invalidate(),
        utils.profiles.list.invalidate(),
        utils.income.list.invalidate(),
        utils.budget.currentYearCashflow.invalidate(),
        utils.fire.getPlan.invalidate(),
        utils.fire.getRecalcStatus.invalidate(),
      ]);
      onClose();
    },
  });
  const invalid = drafts.some(
    (d) =>
      d.mode !== 'auto' &&
      (!/^\d{4}$/.test(d.year) ||
        Number(d.year) < 1900 ||
        Number(d.year) > 2200 ||
        (d.mode === 'retired' && Number(d.year) > currentYear)),
  );
  return (
    <ResponsiveModal
      visible
      onClose={onClose}
      header={
        <Text className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Your retirement arrangements
        </Text>
      }
      maxWidth={560}
    >
      <ScrollView>
        <View className="gap-4 p-4">
          <Text className="text-gray-700 dark:text-gray-300">
            Set each person's arrangement. Retiring is your decision; your
            success target stays the same.
          </Text>
          {view.persons.map((p, index) => {
            const d = drafts[index]!;
            return (
              <View key={p.id} className="gap-2">
                <Text className="font-semibold text-gray-900 dark:text-gray-100">
                  {p.name}
                </Text>
                <View className="flex-row flex-wrap gap-2">
                  {[
                    ['auto', 'Automatic timing'],
                    ['planned', 'Planned year'],
                    ['retired', 'Already retired'],
                  ].map(([mode, label]) => (
                    <Button
                      key={mode}
                      size="sm"
                      variant={d.mode === mode ? 'primary' : 'secondary'}
                      onPress={() =>
                        setDrafts((prev) =>
                          prev.map((row, i) =>
                            i === index ? { ...row, mode: mode! } : row,
                          ),
                        )
                      }
                    >
                      {label}
                    </Button>
                  ))}
                </View>
                {d.mode !== 'auto' && (
                  <TextInput
                    label={
                      d.mode === 'retired'
                        ? 'Actual retirement year'
                        : 'Planned retirement year'
                    }
                    value={d.year}
                    keyboardType="number-pad"
                    maxLength={4}
                    onChangeText={(year) =>
                      setDrafts((prev) =>
                        prev.map((row, i) =>
                          i === index ? { ...row, year } : row,
                        ),
                      )
                    }
                  />
                )}
                {p.actualRetirementYear != null && d.mode !== 'retired' && (
                  <Text className="text-amber-700 dark:text-amber-400">
                    This explicitly changes a recorded retirement. Confirm your
                    work income separately in Income.
                  </Text>
                )}
              </View>
            );
          })}
          {(incomeQ.data ?? [])
            .filter((s) => isWageIncomeType(s.type))
            .map((s) => (
              <Button
                key={s.id}
                size="sm"
                variant={continuingIds.includes(s.id) ? 'primary' : 'secondary'}
                onPress={() =>
                  setContinuing(
                    continuingIds.includes(s.id)
                      ? continuingIds.filter((id) => id !== s.id)
                      : [...continuingIds, s.id],
                  )
                }
              >
                {continuingIds.includes(s.id) ? '✓ ' : ''}Continue {s.name}{' '}
                after retirement
              </Button>
            ))}
          <Text className="text-sm text-gray-600 dark:text-gray-400">
            Calculations use annual retirement boundaries. Review which
            employment income stops. Continuing income from a retired person
            must be explicitly included in your income plan.
          </Text>
          <Button
            variant={confirmed ? 'primary' : 'secondary'}
            onPress={() => setConfirmed(!confirmed)}
          >
            {confirmed
              ? '✓ Assumptions reviewed'
              : 'I reviewed my spending and income assumptions'}
          </Button>
          {incomeQ.error && (
            <Text accessibilityRole="alert" className="text-red-600">
              Income could not be loaded. Reopen this editor before saving
              arrangements.
            </Text>
          )}
          {save.error && (
            <Text accessibilityRole="alert" className="text-red-600">
              {save.error.message}
            </Text>
          )}
          <Button
            loading={save.isPending}
            disabled={invalid || !confirmed || !incomeQ.isSuccess}
            onPress={() =>
              save.mutate({
                choice,
                revision: view.revision,
                continuingIncomeStreamIds: continuingIds,
                schedules: drafts.map((d) => ({
                  personId: d.personId,
                  targetFireYear: d.mode === 'planned' ? Number(d.year) : null,
                  actualRetirementYear:
                    d.mode === 'retired' ? Number(d.year) : null,
                })),
              })
            }
          >
            Save arrangements
          </Button>
          <Button variant="ghost" onPress={onClose}>
            Cancel
          </Button>
        </View>
      </ScrollView>
    </ResponsiveModal>
  );
}
export function FireLifecycleCard() {
  const q = trpc.fire.getLifecycle.useQuery(undefined, {
    refetchInterval: 30000,
  });
  const utils = trpc.useUtils();
  const [choice, setChoice] = useState<RetirementChoice | null>(null);
  const params = useLocalSearchParams<{
    retirementChoice?: RetirementChoice;
  }>();
  const requestedChoice = useRef<string | null>(null);
  useEffect(() => {
    if (
      q.data?.canDecide &&
      params.retirementChoice &&
      ['keep_working', 'plan_retirement', 'already_retired'].includes(
        params.retirementChoice,
      ) &&
      requestedChoice.current !== params.retirementChoice
    ) {
      requestedChoice.current = params.retirementChoice;
      setChoice(params.retirementChoice);
    }
  }, [params.retirementChoice, q.data?.canDecide]);
  const review = trpc.fire.reviewRetirementAssumptions.useMutation({
    onSuccess: async () => {
      await utils.fire.getLifecycle.invalidate();
    },
  });
  const keep = trpc.fire.setRetirementDecision.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.fire.getLifecycle.invalidate(),
        utils.fire.getRecalcStatus.invalidate(),
      ]);
    },
  });
  const view = q.data;
  if (!view?.enabled) return null;
  const a = view.assessment;
  const rate = a ? displayedSuccessRate(a) : null;
  return (
    <View className="gap-3 rounded-xl bg-white dark:bg-gray-800 p-4 my-3">
      <Text className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        {view.milestone ||
        view.persons.some(
          (p) => p.actualRetirementYear != null || p.targetFireYear != null,
        )
          ? `Plan health · ${HEALTH_COPY[view.health]}`
          : 'Retire-now assessment'}
      </Text>
      {a && (
        <>
          <Text className="text-gray-700 dark:text-gray-300">
            {rate == null
              ? 'Success rate unavailable'
              : `${Math.round(rate * 100)}% modeled success`}{' '}
            · Target {Math.round(a.threshold * 100)}%
          </Text>
          <Text className="text-sm text-gray-600 dark:text-gray-400">
            {a.basis === 'retire_now'
              ? 'If all employment income ends in the current projection year.'
              : 'Under your selected household retirement schedule.'}{' '}
            Updated {new Date(a.calculatedAt).toLocaleDateString()}.
          </Text>
          <Text className="text-xs text-gray-500 dark:text-gray-400">
            Success means no unfunded shortfall through {a.endYear - 1} and
            ending net worth above your ${a.estateTarget.toLocaleString()}{' '}
            estate target. These simulated outcomes depend on your assumptions.
          </Text>
          {a.qualityReasons.map((reason) => (
            <Text
              key={reason}
              className="text-sm text-amber-700 dark:text-amber-400"
            >
              {reason}
            </Text>
          ))}
        </>
      )}
      {view.milestone && (
        <Text className="text-green-700 dark:text-green-400">
          🏅 First met your FI target ·{' '}
          {new Date(view.milestone.achievedAt).toLocaleDateString()} ·{' '}
          {Math.round(view.milestone.threshold * 100)}% target
        </Text>
      )}
      {view.persons.map((p) => (
        <Text key={p.id} className="text-sm text-gray-700 dark:text-gray-300">
          {p.name}:{' '}
          {p.actualRetirementYear != null
            ? `retired in ${p.actualRetirementYear}`
            : p.targetFireYear != null
              ? `planned retirement ${p.targetFireYear}${p.targetFireYear <= new Date().getFullYear() ? ' · confirm whether retirement has occurred' : ' · employment assumed until then'}`
              : 'automatic retirement timing'}
        </Text>
      ))}
      {view.canDecide ? (
        <>
          {!view.decision && !!view.milestone && (
            <Text className="text-gray-800 dark:text-gray-200">
              What would you like to do next?
            </Text>
          )}
          <View className="flex-row flex-wrap gap-2">
            <Button
              size="sm"
              loading={keep.isPending}
              variant="secondary"
              onPress={() =>
                keep.mutate({
                  choice: 'keep_working',
                  revision: view.revision,
                  schedules: [],
                })
              }
            >
              Keep working
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onPress={() => setChoice('plan_retirement')}
            >
              Plan my retirement
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onPress={() => setChoice('already_retired')}
            >
              I'm already retired
            </Button>
          </View>
          {(!a ||
            a.qualityReasons.includes(
              'Review your spending and retirement assumptions.',
            )) && (
            <>
              <Text className="text-sm text-gray-600 dark:text-gray-400">
                Review Budget, Income and the retirement schedule above. After
                confirming, recalculate your plan.
              </Text>
              <Button
                size="sm"
                variant="secondary"
                loading={review.isPending}
                onPress={() => review.mutate()}
              >
                Confirm assumptions reviewed
              </Button>
            </>
          )}
          {(keep.error || review.error) && (
            <Text className="text-red-600">
              {keep.error?.message ?? review.error?.message}
            </Text>
          )}
        </>
      ) : (
        <Text className="text-sm text-gray-500">
          Your household's primary member can update retirement arrangements.
        </Text>
      )}
      {choice && (
        <RetirementEditor
          view={view}
          choice={choice}
          onClose={() => setChoice(null)}
        />
      )}
    </View>
  );
}
export function FireCelebrationHost({ enabled }: { enabled: boolean }) {
  const q = trpc.fire.getLifecycle.useQuery(undefined, {
    enabled,
    refetchInterval: 30000,
  });
  const router = useRouter();
  const utils = trpc.useUtils();
  const [lease, setLease] = useState<string | null>(null);
  const requested = useRef<string | null>(null);
  const nextClaimAt = useRef(0);
  const claim = trpc.fire.claimCelebration.useMutation({
    onSuccess: (r) => {
      setLease(r.leaseToken);
      if (!r.leaseToken) {
        requested.current = null;
        nextClaimAt.current = Date.now() + 120000;
      }
    },
    onError: () => {
      requested.current = null;
    },
  });
  const ack = trpc.fire.acknowledgeCelebration.useMutation({
    onSuccess: async () => {
      setLease(null);
      await utils.fire.getLifecycle.invalidate();
    },
  });
  const view = q.data;
  useEffect(() => {
    if (
      enabled &&
      view?.enabled &&
      view.pendingCelebration &&
      view.milestone &&
      requested.current !== view.milestone.id &&
      Date.now() >= nextClaimAt.current
    ) {
      requested.current = view.milestone.id;
      claim.mutate({ milestoneId: view.milestone.id });
    }
  }, [
    enabled,
    view?.enabled,
    view?.pendingCelebration,
    view?.milestone?.id,
    q.dataUpdatedAt,
  ]);
  if (!enabled || !view?.enabled || !view.milestone || !lease) return null;
  const dismiss = () => {
    ack.mutate({ milestoneId: view.milestone!.id, leaseToken: lease });
  };
  return (
    <ResponsiveModal
      visible
      onClose={dismiss}
      header={
        <Text className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          You reached your FI target 🎉
        </Text>
      }
      maxWidth={540}
    >
      <View className="p-5 gap-4">
        <Confetti />
        <Text className="text-gray-800 dark:text-gray-200">
          Your household first met its{' '}
          {Math.round(view.milestone.threshold * 100)}% success target on{' '}
          {new Date(view.milestone.achievedAt).toLocaleDateString()}.
        </Text>
        <Text className="text-gray-600 dark:text-gray-400">
          Celebrate this milestone, then review your current plan. Your latest
          outlook may have changed since this achievement.
        </Text>
        {view.canDecide && (
          <View className="gap-2">
            {(
              [
                ['keep_working', 'Keep working'],
                ['plan_retirement', 'Plan my retirement'],
                ['already_retired', "I'm already retired"],
              ] as const
            ).map(([choice, label]) => (
              <Button
                key={choice}
                variant="secondary"
                onPress={() => {
                  dismiss();
                  router.push({
                    pathname: '/(app)/fire-plan',
                    params: { retirementChoice: choice },
                  });
                }}
              >
                {label}
              </Button>
            ))}
          </View>
        )}
        <Button
          onPress={() => {
            dismiss();
            router.push('/(app)/fire-plan');
          }}
        >
          View your plan
        </Button>
        <Button variant="ghost" loading={ack.isPending} onPress={dismiss}>
          Celebrate and close
        </Button>
        {ack.error && <Text className="text-red-600">{ack.error.message}</Text>}
      </View>
    </ResponsiveModal>
  );
}
